import { isToday } from 'date-fns';

import { AuthErrorData, isAuthenticatedLogin, LoginResponse, MFAMethod, MFASendResponse } from '../../auth/types';
import {
  clearAuthData,
  getRole,
  getRules,
  getRulesExpiration,
  removeImpersonationExpiresAt,
  removeRules,
  setRole,
  setRules,
} from '../../utils/helpers/storage';
import { deleteRequest, getRequest, getWithParamsRequest, postRequest, putRequest } from '../utils/provider';
import { ApiResponse } from '../utils/responseTransform';

function beforeLogin() {
  removeImpersonationExpiresAt();
}

function afterLogin(response: ApiResponse<LoginResponse, AuthErrorData>) {
  if (response?.type === 'success' && isAuthenticatedLogin(response.data)) {
    setRules(response.data.rules);
  }
}

export type SignUpResponseType = {
  message: string;
  state: 'active' | 'pending_approval';
};

export async function login(
  email: string,
  password: string,
  signal?: AbortSignal
): Promise<ApiResponse<LoginResponse, AuthErrorData>> {
  const payload = {
    include: 'user',
    organizer: { email, password },
  };

  beforeLogin();
  const response = await postRequest<LoginResponse, AuthErrorData>('organizers/sign_in', payload, signal);

  if (!signal?.aborted) {
    afterLogin(response);
  }

  return response;
}

export async function loginMFA(code: string, signal?: AbortSignal): Promise<ApiResponse<LoginResponse, AuthErrorData>> {
  const payload = {
    include: 'user',
    organizer: { otp_attempt: code },
  };

  beforeLogin();
  const response = await postRequest<LoginResponse, AuthErrorData>('organizers/sign_in', payload, signal, true);

  if (!signal?.aborted) {
    afterLogin(response);
  }

  return response;
}

export async function resendMFA(
  method?: MFAMethod,
  signal?: AbortSignal
): Promise<ApiResponse<MFASendResponse, AuthErrorData>> {
  return postRequest('multi_factor_authentications', method ? { mfa: { method } } : {}, signal, true);
}

export async function welcome(token: string): Promise<ApiResponse<{ user: UserFormData & { ssoRequired?: boolean } }>> {
  return getWithParamsRequest('organizers/welcome', { confirmationToken: token });
}

export async function signUp(payload): Promise<ApiResponse<SignUpResponseType>> {
  return postRequest('organizers/sign_up', payload);
}

// Note: I will add this function to be use once the profile popup dialog has been merged
export async function logout(): Promise<ApiResponse> {
  const response = await deleteRequest<ApiResponse>('organizers/sign_out');

  clearAuthData();

  return response;
}

export async function forgotPassword(payload): Promise<ApiResponse<{ message: string }>> {
  return postRequest('organizers/password', payload);
}

export async function resetPassword(payload): Promise<ApiResponse<{ message: string }>> {
  return putRequest('organizers/password', payload);
}

export async function stopImpersonation() {
  try {
    await getRequest('stop_impersonation');
    clearAuthData();

    return true;
  } catch (error) {
    return error;
  }
}

export async function getUserProfile(): Promise<ApiResponse<User>> {
  const expiration = getRulesExpiration();
  const rules = getRules();
  const role = getRole();

  const shouldFetchRules = expiration && rules ? isToday(expiration) : true;

  const response = await getRequest<User>(`organizers/profile?${shouldFetchRules ? 'include=rules' : ''}`);

  if (response?.type === 'success') {
    if (shouldFetchRules) {
      setRules(response.data.rules);
    }

    const hasRoleChanged = response.data.roles[0] != role;

    setRole(response.data.roles[0]);

    if (hasRoleChanged) {
      removeRules();

      return getUserProfile();
    }
  }

  return response;
}

export async function getUserIdentity(): Promise<ApiResponse<Identity>> {
  return getRequest('organizers/whoami');
}
